import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Chip,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormGroup,
  Tooltip,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../../utils/dateLocale';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { CreateEventRequest, CreateEventSeriesRequest, EventParticipantRequest, EventSeriesConflict, Venue, EventTypeCalendar } from '../../types/calendar';
import { UserListItem, UserRole } from '../../types/user';
import { userOptionLabel } from '../../utils/userLabel';
import { PlannedRecurringEvent } from '../../types/plannedEvents';
import { calendarService } from '../../services/calendarService';
import { userService } from '../../services/userService';
import plannedEventService from '../../services/plannedEventService';
import { useAuth } from '../../contexts/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { getTodayLocal } from '../../utils/dateUtils';
// [3. web-validátor BLOKKOLÓ] a dátummezők saját piszkozat-értékkel (a félkész gépelés nem írja vissza / át a hónapot).
import DraftDatePicker, { DraftDateStatus, draftDateError } from './DraftDatePicker';
import { SESSION_EXPIRED_MESSAGE } from '../../utils/session';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';

// [magánóra-sorozat, 2026-10-31] MI VOLT A BAJ: a sorozatot a KLIENS számolta (`recurringEventDates`) és N darab
// `POST /events`-ből rakta össze (`createRecurringEvents` + `createSeriesEvents`, a korábbi 579-595. sor körül),
// kliens-generált csoport-azonosítóval, részleges sikerrel. Most a SZERVER számol és ellenőriz: EGY
// `POST /events/series` (admin ÉS edző), előnézet a `dryRun`-ból (`./SeriesPreview`). A
// `utils/recurringSeries.ts` dátum-függvényei innen KIVEZETVE (csak a hétnap-nevek maradtak).
import { DAY_NAMES_MONDAY_FIRST } from '../../utils/recurringSeries';
import { dayNamesToNumbers, seriesConflictsFromError, MAX_SERIES_OCCURRENCES } from '../../utils/eventSeries';
import { SeriesPreviewAlert, SeriesConflictAlert, useSeriesPreview, seriesPreviewBlocksSave } from './SeriesPreview';

/** A sorozat felső határa — a közös konstans (`utils/eventSeries.ts`), innen is exportálva. */
export { MAX_SERIES_OCCURRENCES };

/**
 * Az ÜRES (még ki nem töltött) űrlap — egyetlen helyen, hogy a kezdőállapot, a `resetForm()` és a
 * mentetlen-változás ALAPVONALA soha ne csúszhasson el egymástól. (Korábban a komponens
 * kezdőállapota 1 asztalt, a `resetForm` 0-t állított — pont ilyen elcsúszás.)
 */
export function emptyEventCreateForm(initial: {
  initialDate?: string;
  initialStartTime?: string;
  initialEndTime?: string;
}): CreateEventRequest {
  return {
    eventName: '',
    eventDate: initial.initialDate || getTodayLocal(),
    startTime: initial.initialStartTime || '09:00:00',
    endTime: initial.initialEndTime || '10:00:00',
    eventTypeId: '',
    venueId: '',
    plannedEventId: undefined,
    description: '',
    maxParticipants: undefined,
    // 1 és nem 0: a leggyakoribb eset egyetlen asztal, és a 0 üres mezőként jelenne meg,
    // amit a felhasználónak minden eseménynél kézzel kellene kitöltenie (a mentés
    // különben „Legalább 1 asztal megadása kötelező" hibára fut).
    requiredTables: 1,
    participants: [],
  };
}

interface EventCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onEventCreated?: () => void;
  initialDate?: string;
  initialStartTime?: string;
  initialEndTime?: string;
}

const EventCreateDialog: React.FC<EventCreateDialogProps> = ({
  open,
  onClose,
  onEventCreated,
  initialDate,
  initialStartTime,
  initialEndTime,
}) => {
  const [formData, setFormData] = useState<CreateEventRequest>(() =>
    emptyEventCreateForm({ initialDate, initialStartTime, initialEndTime })
  );

  const [venues, setVenues] = useState<Venue[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeCalendar[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [plannedEvents, setPlannedEvents] = useState<PlannedRecurringEvent[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserListItem[]>([]);
  const [userRoleAssignments, setUserRoleAssignments] = useState<{[userId: string]: string}>({});

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [availableTables, setAvailableTables] = useState<{[venueId: string]: number}>({});

  // Recurring event state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringEndDate, setRecurringEndDate] = useState<string>('');
  /**
   * [magánóra-sorozat, 2026-10-31] A mentés 400-as ütközés-válaszának napjai (a szerver minden-vagy-semmi:
   * ütközésnél SEMMI nem jön létre). A korábbi „részleges sorozat" állapot (`seriesPartial` +
   * `baselineOverride` alapvonal-léptetés [N-15]) ezzel MEGSZŰNT: részleges siker már nem létezik, tehát az
   * űrlap tartalma bukás után is valódi mentetlen adat — a védelem változatlanul kérdez.
   */
  const [submitConflicts, setSubmitConflicts] = useState<EventSeriesConflict[]>([]);
  /**
   * [3. web-validátor] A két dátummező PISZKOZATÁNAK állapota (`DraftDatePicker`). Félbehagyott / érvénytelen gépelésnél
   * a mentés NEM a régi értékkel megy ki csendben: a `validateForm` hibát ad, és a sorozat-előnézet sem fut.
   */
  const [dateDraft, setDateDraft] = useState<{ eventDate?: DraftDateStatus; recurringEndDate?: DraftDateStatus }>({});
  /** Edzőnél a saját maga előre felvétele (Coach-szerepkörrel) megtörtént-e ebben a megnyitásban. */
  const [coachSelfPrepared, setCoachSelfPrepared] = useState(false);
  /** Ismétlődés gyakorisága hetekben: 1 = minden héten, 2 = kéthetente, ... */
  const [recurringIntervalWeeks, setRecurringIntervalWeeks] = useState<number>(1);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<string[]>([]);

  // Edző KIZÁRÓLAG magánórát (IndividualTraining) hozhat létre, sablon nélkül. A típus-lista erre
  // szűkül, a sablon-választó rejtve, és a magánóra automatikusan előválasztódik. Backend a mérvadó. [klub-kérés]
  const { user } = useAuth();
  const { showSuccess } = useFeedback();
  const confirm = useConfirm();
  const isCoach = user?.userRole === 'Coach';
  const visibleEventTypes = isCoach ? eventTypes.filter(t => t.name === 'IndividualTraining') : eventTypes;
  const selfId = user?.userId;
  const coachRoleId = userRoles.find(r => r.name === 'Coach')?.userRoleId;
  /**
   * [magánóra-sorozat, 2026-10-31, user 4. döntés] Edzőnél SOROZATBAN a saját maga Coach-résztvevőként
   * KÖTELEZŐ (a szerver különben 403-at ad: `EventController.cs` `CoachSeriesGuard`), ezért nem távolítható el;
   * egyszeri magánóránál eltávolítható (a régi viselkedés).
   */
  const selfLocked = isCoach && isRecurring;

  /**
   * Mentetlen-változás védelem. A „piszkos" itt azt jelenti: a felhasználó BEÍRT valamit, amit
   * a bezárás elvetne. Az alapvonal ezért nem a puszta üres űrlap, hanem az ELŐKÉSZÍTETT
   * állapot: a naptárból örökölt dátum/idő és — edzőnél — az automatikusan előválasztott
   * magánóra-típus + 1 fős létszám NEM felhasználói bevitel, ezért nem is szabad kérdezni miattuk.
   */
  const preparedEventTypeId = isCoach
    ? (eventTypes.find(t => t.name === 'IndividualTraining')?.eventTypeId ?? '')
    : '';
  const formSnapshot = {
    form: formData,
    selectedUserIds: selectedUsers.map(u => u.userId),
    userRoleAssignments,
    isRecurring,
    recurringEndDate,
    recurringIntervalWeeks,
    selectedDaysOfWeek,
  };
  const baselineSnapshot: typeof formSnapshot = {
    form: {
      ...emptyEventCreateForm({ initialDate, initialStartTime, initialEndTime }),
      eventTypeId: preparedEventTypeId,
      maxParticipants: preparedEventTypeId ? 1 : undefined,
    },
    // [magánóra-sorozat, 2026-10-31] Edzőnél az ELŐKÉSZÍTETT állapot része a saját maga Coach-résztvevőként
    // (a mobil mintája, `mobile/app/events/create.tsx:138-143`) — nem felhasználói bevitel, tehát nem kérdezhet.
    selectedUserIds: coachSelfPrepared && selfId ? [selfId] : [],
    userRoleAssignments: coachSelfPrepared && selfId && coachRoleId ? { [selfId]: coachRoleId } : {},
    isRecurring: false,
    recurringEndDate: '',
    recurringIntervalWeeks: 1,
    selectedDaysOfWeek: [],
  };
  /**
   * `open &&`: a dialógus zárva is MOUNTOLVA marad a szülőben az utolsó (elvetett) tartalommal —
   * zárt ablakra nem szabad mentetlen adatot jelenteni, mert az a fül-váltást blokkolná.
   */
  const isDirty = open && isFormDirty(baselineSnapshot, formSnapshot);
  useUnsavedChanges(isDirty, 'Új esemény');

  /** A Dialog `onClose`-a (backdrop + ESC) és a „Mégsem" gomb közös, védett záró útja. */
  const handleClose = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm('új esemény űrlapja')))) {
      return;
    }
    onClose();
  };

  useEffect(() => {
    if (open) {
      loadData();
      resetForm();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edzőnél a magánóra típus automatikus előválasztása
  useEffect(() => {
    if (open && isCoach && !formData.eventTypeId) {
      const magan = eventTypes.find(t => t.name === 'IndividualTraining');
      // Az 1 fős alapérték itt is kell: az edzőnél a típus előre ki van választva,
      // tehát a fenti onChange sosem fut le.
      if (magan) setFormData(prev => ({
        ...prev,
        eventTypeId: magan.eventTypeId,
        maxParticipants: prev.maxParticipants ?? 1,
      }));
    }
  }, [open, isCoach, eventTypes, formData.eventTypeId]);

  /**
   * A bejelentkezett edző saját maga a résztvevők közé, Coach-szerepkörrel. Ha a tag-listában nincs benne
   * (nem várt), a saját adataiból pótoljuk, hogy a kliens-oldali ellenőrzés („Érvénytelen felhasználó") ne bukjon.
   */
  const ensureSelfAsCoach = () => {
    if (!selfId || !coachRoleId || !user) return;
    const listed = users.find(u => u.userId === selfId);
    const self: UserListItem = listed ?? ({
      userId: selfId,
      name: user.name,
      email: user.email ?? null,
      phone: null,
      userRole: 'Coach',
      userRoleDisplayName: 'Edző',
      isActive: true,
    } as unknown as UserListItem);
    if (!listed) setUsers(prev => (prev.some(u => u.userId === selfId) ? prev : [...prev, self]));
    setSelectedUsers(prev => (prev.some(u => u.userId === selfId) ? prev : [self, ...prev]));
    setUserRoleAssignments(prev => ({ ...prev, [selfId]: coachRoleId }));
  };

  // [magánóra-sorozat, 2026-10-31] Edzőnél megnyitáskor (az adatok betöltése után) EGYSZER előre felvesszük.
  useEffect(() => {
    if (!open || !isCoach || coachSelfPrepared || dataLoading || !selfId || !coachRoleId) return;
    ensureSelfAsCoach();
    setCoachSelfPrepared(true);
  }, [open, isCoach, coachSelfPrepared, dataLoading, selfId, coachRoleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialDate) {
      setFormData(prev => ({ ...prev, eventDate: initialDate }));
    }
    if (initialStartTime) {
      setFormData(prev => ({ ...prev, startTime: initialStartTime }));
    }
    if (initialEndTime) {
      setFormData(prev => ({ ...prev, endTime: initialEndTime }));
    }
  }, [initialDate, initialStartTime, initialEndTime]);

  // Calculate available tables when venue, date, or time changes
  useEffect(() => {
    if (formData.venueId && formData.eventDate && formData.startTime && formData.endTime) {
      console.log('Recalculating available tables due to form changes:', {
        venueId: formData.venueId,
        eventDate: formData.eventDate,
        startTime: formData.startTime,
        endTime: formData.endTime
      });
      calculateAvailableTables();
    } else {
      // Clear available tables if required fields are missing
      setAvailableTables({});
    }
  }, [formData.venueId, formData.eventDate, formData.startTime, formData.endTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setFormData(emptyEventCreateForm({ initialDate, initialStartTime, initialEndTime }));
    setSelectedUsers([]);
    setUserRoleAssignments({});
    setError('');
    setValidationErrors({});
    setAvailableTables({});
    setIsRecurring(false);
    setSubmitConflicts([]);
    setCoachSelfPrepared(false);
    setRecurringEndDate('');
    setRecurringIntervalWeeks(1);
    setSelectedDaysOfWeek([]);
  };

  const calculateAvailableTables = async (customFormData?: any) => {
    // Use custom form data if provided, otherwise use current state
    const dataToUse = customFormData || formData;

    if (!dataToUse.venueId || !dataToUse.eventDate || !dataToUse.startTime || !dataToUse.endTime) {
      console.log('Skipping table calculation - missing required fields:', {
        venueId: dataToUse.venueId,
        eventDate: dataToUse.eventDate,
        startTime: dataToUse.startTime,
        endTime: dataToUse.endTime
      });
      return;
    }

    try {
      console.log('Calculating available tables for:', {
        venueId: dataToUse.venueId,
        date: dataToUse.eventDate,
        startTime: dataToUse.startTime,
        endTime: dataToUse.endTime
      });

      // Get events for the specific day to check table usage
      const dayEvents = await calendarService.getDailyEvents(dataToUse.eventDate);
      console.log('Day events retrieved:', dayEvents);

      const selectedVenue = venues.find(v => v.venueId === dataToUse.venueId);
      if (!selectedVenue) {
        console.log('Selected venue not found');
        return;
      }

      const venueTotalTables = selectedVenue.tableCount || 10;
      console.log('Venue total tables:', venueTotalTables);

      // Filter events that overlap with the selected time range and are at the same venue
      const conflictingEvents = dayEvents.filter(event => {
        if (event.venueId !== dataToUse.venueId) return false;

        // Check time overlap
        const eventStart = new Date(`2000-01-01T${event.startTime}`);
        const eventEnd = new Date(`2000-01-01T${event.endTime}`);
        const newEventStart = new Date(`2000-01-01T${dataToUse.startTime}`);
        const newEventEnd = new Date(`2000-01-01T${dataToUse.endTime}`);

        // Events overlap if: (start1 < end2) && (start2 < end1)
        const overlaps = eventStart < newEventEnd && newEventStart < eventEnd;
        if (overlaps) {
          console.log('Conflicting event found:', {
            eventName: event.eventName,
            eventStart: event.startTime,
            eventEnd: event.endTime,
            requiredTables: event.requiredTables
          });
        }
        return overlaps;
      });

      // Sum up required tables from conflicting events
      const usedTables = conflictingEvents.reduce((sum, event) => sum + (event.requiredTables || 0), 0);
      const availableTablesCount = Math.max(0, venueTotalTables - usedTables);

      console.log('Table calculation result:', {
        totalTables: venueTotalTables,
        usedTables,
        availableTables: availableTablesCount,
        conflictingEventsCount: conflictingEvents.length
      });

      setAvailableTables(prev => ({
        ...prev,
        [dataToUse.venueId]: availableTablesCount
      }));

    } catch (error) {
      console.error('Error calculating available tables:', error);
      // Default to venue capacity if calculation fails
      const selectedVenue = venues.find(v => v.venueId === dataToUse.venueId);
      if (selectedVenue) {
        setAvailableTables(prev => ({
          ...prev,
          [dataToUse.venueId]: selectedVenue.tableCount || 10
        }));
      }
    }
  };

  const loadData = async () => {
    try {
      setDataLoading(true);
      setError('');
      console.log('Loading reference data...');

      // Check authentication before loading data
      const token = localStorage.getItem('token');
      if (!token) {
        // [Web UX 2. csomag, 2026-10-03] Egységes, tegező mondat (a közös lejárat-kezelés szövege).
        setError(SESSION_EXPIRED_MESSAGE);
        return;
      }

      // Load venues
      try {
        console.log('Loading venues...');
        const venuesData = await calendarService.getActiveVenues();
        console.log('Venues loaded:', venuesData);
        setVenues(venuesData);
      } catch (err) {
        console.error('Error loading venues:', err);
        setVenues([]);
      }

      // Load event types
      try {
        console.log('Loading event types...');
        const eventTypesData = await calendarService.getActiveEventTypes();
        console.log('Event types loaded:', eventTypesData);
        setEventTypes(eventTypesData);
      } catch (err) {
        console.error('Error loading event types:', err);
        setEventTypes([]);
      }

      // Load users
      try {
        console.log('Loading users...');
        const result = await userService.getUsers();
        // Filter for both approved and active users for event participation
        const approvedActiveUsers = result.data.filter((user: UserListItem) => user.isActive);
        console.log('Users loaded:', approvedActiveUsers);
        setUsers(approvedActiveUsers);
      } catch (err) {
        console.error('Error loading users:', err);
        setUsers([]);
      }

      // Load user roles
      try {
        console.log('Loading user roles...');
        const rolesData = await userService.getUserRoles();
        const activeRoles = rolesData.filter((role: UserRole) => role.isActive && role.name !== 'Administrator');
        console.log('User roles loaded:', activeRoles);
        setUserRoles(activeRoles);
      } catch (err) {
        console.error('Error loading user roles:', err);
        setUserRoles([]);
      }

      // Load planned events
      try {
        console.log('Loading planned events...');
        const plannedEventsData = await plannedEventService.getPlannedEvents();
        const activePlannedEvents = plannedEventsData.filter((event: PlannedRecurringEvent) => event.isActive);
        console.log('Planned events loaded:', activePlannedEvents);
        setPlannedEvents(activePlannedEvents);
      } catch (err) {
        console.error('Error loading planned events:', err);
        setPlannedEvents([]);
      }

      console.log('Data loading completed successfully');
    } catch (err) {
      console.error('Error loading data:', err);
      // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
      // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
      setError(requestErrorMessage(err, 'Hiba történt az adatok betöltésekor'));
    } finally {
      setDataLoading(false);
    }
  };

  const validateRequiredTables = (tableCount: number): boolean => {
    return validateRequiredTablesWithData(tableCount, formData);
  };

  const validateRequiredTablesWithData = (tableCount: number, dataToUse: any): boolean => {
    if (tableCount == null || tableCount < 0) {
      return false;
    }

    if (dataToUse.venueId) {
      // Check if required tables exceed available capacity
      const selectedVenue = venues.find(v => v.venueId === dataToUse.venueId);
      if (selectedVenue) {
        const currentlyAvailable = availableTables[dataToUse.venueId] !== undefined ?
          availableTables[dataToUse.venueId] : (selectedVenue.tableCount || 10);

        if (tableCount > currentlyAvailable) {
          return false;
        }
      }
    }

    return true;
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Don't validate if data is still loading
    if (dataLoading) {
      console.log('Skipping validation - data is still loading');
      return false;
    }

    if (!formData.eventName.trim()) {
      errors.eventName = 'Az esemény neve kötelező';
    }

    if (!formData.eventTypeId) {
      errors.eventTypeId = 'Az esemény típusa kötelező';
    } else {
      // Validate eventTypeId exists in the loaded event types
      const eventTypeExists = eventTypes.some(type => type.eventTypeId === formData.eventTypeId);
      if (!eventTypeExists) {
        errors.eventTypeId = 'A kiválasztott esemény típus nem érvényes';
        console.warn('Invalid event type ID:', formData.eventTypeId, 'Available event types:', eventTypes);
      }
    }

    if (!formData.venueId) {
      errors.venueId = 'A helyszín kiválasztása kötelező';
    } else {
      // Validate venueId exists in the loaded venues
      const venueExists = venues.some(venue => venue.venueId === formData.venueId);
      if (!venueExists) {
        errors.venueId = 'A kiválasztott helyszín nem érvényes';
        console.warn('Invalid venue ID:', formData.venueId, 'Available venues:', venues);
      }
    }

    const startTime = new Date(`2000-01-01T${formData.startTime}`);
    const endTime = new Date(`2000-01-01T${formData.endTime}`);

    if (startTime >= endTime) {
      errors.endTime = 'A befejezés időpontja későbbi kell legyen a kezdésnél';
    }

    if (formData.requiredTables == null || formData.requiredTables < 1) {
      errors.requiredTables = 'Legalább 1 asztal megadása kötelező';
    } else if (formData.venueId) {
      // Check if required tables exceed available capacity
      const selectedVenue = venues.find(v => v.venueId === formData.venueId);
      if (selectedVenue) {
        const currentlyAvailable = availableTables[formData.venueId] !== undefined ?
          availableTables[formData.venueId] : (selectedVenue.tableCount || 10);

        if (formData.requiredTables > currentlyAvailable) {
          errors.requiredTables = `Csak ${currentlyAvailable} asztal érhető el a kiválasztott időpontban`;
        }
      }
    }

    if (selectedUsers.length === 0) {
      errors.participants = 'Legalább egy résztvevőt ki kell választani';
    } else {
      // Check if all selected users have assigned roles
      const missingRoles = selectedUsers.filter(user => !userRoleAssignments[user.userId]);
      if (missingRoles.length > 0) {
        errors.participants = 'Minden résztvevőhöz szerepkört kell rendelni';
      } else {
        // Validate userIds exist in the loaded users
        for (const user of selectedUsers) {
          const userExists = users.some(u => u.userId === user.userId);
          if (!userExists) {
            errors.participants = 'Érvénytelen felhasználó kiválasztása';
            console.warn('Invalid user ID:', user.userId, 'Available users:', users);
            break;
          }

          const roleId = userRoleAssignments[user.userId];
          if (roleId) {
            const roleExists = userRoles.some(role => role.userRoleId === roleId);
            if (!roleExists) {
              errors.participants = 'Érvénytelen szerepkör kiválasztása';
              console.warn('Invalid role ID:', roleId, 'Available roles:', userRoles);
              break;
            }
          }
        }
      }
    }

    // [3. web-validátor] a „Dátum" piszkozata: érvénytelen vagy üres → nincs mentés (nem a régi értékkel megy ki).
    const eventDateDraftError = draftDateError(dateDraft.eventDate, true);
    if (eventDateDraftError) {
      errors.eventDate = eventDateDraftError;
    }

    // Validate recurring event settings
    if (isRecurring) {
      const untilDraftError = draftDateError(dateDraft.recurringEndDate, false);
      if (untilDraftError) {
        errors.recurringEndDate = untilDraftError;
      } else if (!recurringEndDate) {
        errors.recurringEndDate = 'Az ismétlődés végdátuma kötelező';
      } else {
        const startDate = new Date(formData.eventDate);
        const endDate = new Date(recurringEndDate);
        if (endDate <= startDate) {
          errors.recurringEndDate = 'A végdátum későbbi kell legyen a kezdő dátumnál';
        }
      }

      if (selectedDaysOfWeek.length === 0) {
        errors.selectedDaysOfWeek = 'Legalább egy napot ki kell választani';
      }
    }

    console.log('Form validation errors:', errors);
    console.log('Form data being validated:', {
      eventTypeId: formData.eventTypeId,
      venueId: formData.venueId,
      selectedUsers: selectedUsers.map(u => ({ userId: u.userId, name: u.name })),
      userRoleAssignments,
      availableEventTypes: eventTypes.length,
      availableVenues: venues.length,
      availableUsers: users.length,
      availableRoles: userRoles.length,
      isRecurring,
      recurringEndDate,
      selectedDaysOfWeek
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * [magánóra-sorozat, 2026-10-31] A sorozat-kérés EGY helyen: ugyanez megy az előnézetbe (`dryRun: true`) és
   * a mentésbe (`dryRun: false`), így az előnézet nem mondhat mást, mint ami létrejön.
   * `null` = hiányos az űrlap, nincs mit előnézni (a szerver úgyis 400-at adna).
   */
  const buildSeriesRequest = (): Omit<CreateEventSeriesRequest, 'dryRun'> | null => {
    if (!isRecurring || !recurringEndDate || selectedDaysOfWeek.length === 0) return null;
    // Félkész / érvénytelen dátum-piszkozatnál nincs előnézet (a régi értékkel félrevezető lenne).
    if (dateDraft.eventDate && dateDraft.eventDate !== 'valid') return null;
    if (dateDraft.recurringEndDate && dateDraft.recurringEndDate !== 'valid') return null;
    if (recurringEndDate <= formData.eventDate) return null;
    if (!formData.eventName.trim() || !formData.venueId || !formData.eventTypeId) return null;
    if (!(formData.startTime < formData.endTime)) return null;
    if (!formData.requiredTables || formData.requiredTables < 1) return null;
    if (selectedUsers.length === 0 || selectedUsers.some(u => !userRoleAssignments[u.userId])) return null;
    return {
      eventName: formData.eventName,
      eventDate: formData.eventDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      eventTypeId: formData.eventTypeId,
      venueId: formData.venueId,
      plannedEventId: formData.plannedEventId,
      description: formData.description,
      maxParticipants: formData.maxParticipants,
      requiredTables: formData.requiredTables,
      participants: selectedUsers.map(u => ({ userId: u.userId, userRoleId: userRoleAssignments[u.userId] })),
      repeatUntil: recurringEndDate,
      intervalWeeks: recurringIntervalWeeks,
      daysOfWeek: dayNamesToNumbers(selectedDaysOfWeek),
    };
  };
  const seriesRequest = open && !dataLoading ? buildSeriesRequest() : null;
  const seriesPreview = useSeriesPreview(seriesRequest, (req) => calendarService.createSeries({ ...req, dryRun: true }));
  const saveBlockedBySeries = isRecurring && seriesPreviewBlocksSave(seriesPreview);
  /**
   * [1. web-validátor M2] A mentés 400-as ütközés-listája ELAVUL, amint bármely sorozat-releváns mező változik
   * (név, idő, helyszín, típus, asztal, résztvevők, napok, gyakoriság, „eddig") — ilyenkor eltűnik (az előnézet
   * úgyis újraszámol). A kulcs a sorozat-kérés tartalma; hiányos űrlapnál a nyers mezők.
   */
  const seriesConflictKey = JSON.stringify(seriesRequest ?? {
    isRecurring, formData, selectedDaysOfWeek, recurringEndDate, recurringIntervalWeeks,
    selectedUserIds: selectedUsers.map(u => u.userId), userRoleAssignments,
  });
  useEffect(() => {
    setSubmitConflicts([]);
  }, [seriesConflictKey]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const participants: EventParticipantRequest[] = selectedUsers.map(user => ({
        userId: user.userId,
        userRoleId: userRoleAssignments[user.userId],
      }));

      const eventData: CreateEventRequest = {
        ...formData,
        participants,
      };

      console.log('Creating event with data:', eventData);
      console.log('Selected users:', selectedUsers);
      console.log('User role assignments:', userRoleAssignments);

      // Check authentication status before making API call
      const token = localStorage.getItem('token');
      if (!token) {
        // [Web UX 2. csomag, 2026-10-03] Egységes, tegező mondat (a közös lejárat-kezelés szövege).
        setError(SESSION_EXPIRED_MESSAGE);
        return;
      }

      console.log('Making API call to create event...');

      // Sorozatnál a sikeres visszajelzés a ténylegesen létrejött alkalmak számát mondja ki
      // (nem csak "sikerült"), mert a felhasználót ez érdekli, ha pl. kéthetente 4 nap jött létre.
      let createdSeriesCount: number | null = null;
      setSubmitConflicts([]);

      if (isRecurring && recurringEndDate && selectedDaysOfWeek.length > 0) {
        // [magánóra-sorozat, 2026-10-31] PONTOSAN EGY `POST /events/series` (admin és edző is) — a szerver
        // számolja a napokat, adja a csoport-azonosítót, és ütközésnél SEMMIT nem hoz létre (400 + conflicts).
        const request = buildSeriesRequest();
        if (!request) {
          setError('Az ismétlődés beállításai hiányosak — ellenőrizd a napokat és az „eddig" dátumot.');
          return;
        }
        const created = await calendarService.createSeries({ ...request, dryRun: false });
        createdSeriesCount = created.created.length;
      } else {
        // Egyszeri esemény — a régi `POST /events` út, változatlanul.
        const result = await calendarService.createEvent(eventData);
        console.log('Event created successfully:', result);
      }

      // Show success feedback
      console.log('Event creation completed, calling callbacks...');
      showSuccess(
        createdSeriesCount != null
          ? `${createdSeriesCount} alkalom létrehozva.`
          : 'Az esemény létrehozva.'
      );
      // Recalculate available tables after event creation
      await calculateAvailableTables();
      onEventCreated?.();
      onClose();
    } catch (err: any) {
      console.error('Error creating event:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);

      // [Web UX 2. csomag – 3. javító kör, validátor F-1, 2026-10-03] A 401-et a KÖZÖS jelző kezeli
      // (`utils/session.ts` → `AuthContext`): mentetlen adatnál újra-belépő ablak, a beírt űrlap
      // megmarad. A 2. kör itt MINDEN 401-re a „megmaradtak" mondatot írta (a zár vizsgálata nélkül),
      // így egy elavult (közben lecserélt tokenű) 401 is ezt kapta, ablak nélkül. Most a zár-tudatos
      // `saveErrorMessage` dönt: zár nélkül a szokásos „… (HTTP 401)" marad.
      const status = err?.response?.status;
      const fallback =
        status === 400
          ? 'Érvénytelen adatok. Kérjük ellenőrizze a mezőket.'
          : status === 500
            ? 'Szerver hiba. Kérjük próbálja meg később.'
            : 'Hiba történt az esemény létrehozásakor';
      let errorMessage = saveErrorMessage(err, fallback);
      // [magánóra-sorozat, 2026-10-31] Ütközésnél (400) a szerver az ütköző napokat is megadja — külön listában.
      if (isRecurring) setSubmitConflicts(seriesConflictsFromError(err));
      // Nem kérés-hiba (a kezelőben dobott saját `Error`): a régi viselkedés szerint a saját szövege.
      if (!err?.response && !err?.isAxiosError && !err?.request && typeof err?.message === 'string' && err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (picked: UserListItem[]) => {
    // [magánóra-sorozat, 2026-10-31] Edzőnél sorozatban a saját maga NEM távolítható el (törlő ikon, „mindet
    // töröl" gomb, Backspace — mind ide fut). A szerver úgyis 403-at adna; itt meg sem engedjük.
    let users = picked;
    if (selfLocked && selfId && !picked.some(u => u.userId === selfId)) {
      const self = selectedUsers.find(u => u.userId === selfId);
      if (self) users = [self, ...picked];
    }
    setSelectedUsers(users);

    // Remove role assignments for users that are no longer selected
    const newRoleAssignments = { ...userRoleAssignments };
    Object.keys(newRoleAssignments).forEach(userId => {
      if (!users.find(user => user.userId === userId)) {
        delete newRoleAssignments[userId];
      }
    });

    // Auto-assign role based on user's system role for newly added users
    users.forEach(user => {
      if (!newRoleAssignments[user.userId]) {
        const matchingRole = userRoles.find(role => role.name === user.userRole);
        if (matchingRole) {
          newRoleAssignments[user.userId] = matchingRole.userRoleId;
        }
      }
    });

    setUserRoleAssignments(newRoleAssignments);
  };

  const handleUserRoleChange = (userId: string, roleId: string) => {
    setUserRoleAssignments(prev => ({
      ...prev,
      [userId]: roleId,
    }));
  };

  const parseTimeString = (timeString: string) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return dayjs().set('hour', hours).set('minute', minutes).set('second', 0);
  };

  const formatTimeString = (date: dayjs.Dayjs): string => {
    return date.format('HH:mm:ss');
  };

  return (
    // [Web UX 9 / T2, 2026-10-13] `adapterLocale="hu"` PÓTOLVA: nélküle az AdapterDayjs KÉNYSZERÍTETTEN angol
    // (`getCurrentLocaleCode: this.locale || 'en'`) — angol hónapnevek („October 2026"), vasárnapos hét, amerikai MM/DD/YYYY mező.
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu" localeText={HU_PICKER_LOCALE_TEXT}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Új esemény létrehozása
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {submitConflicts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <SeriesConflictAlert conflicts={submitConflicts} testId="series-create-submit-conflicts" />
            </Box>
          )}

          {dataLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Adatok betöltése...
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Sablon-választó (Tervezett esemény) — edzőnek REJTVE: csak magánóra, sablon nélkül [klub-kérés] */}
            {!isCoach && (
            <Autocomplete
              options={plannedEvents}
              getOptionLabel={(option) => `${option.eventName} - ${option.dayOfWeek} ${option.startTime.substring(0, 5)}-${option.endTime.substring(0, 5)}`}
              value={plannedEvents.find(pe => pe.plannedEventId === formData.plannedEventId) || null}
              onChange={async (_, newValue) => {
                if (newValue) {
                  // Auto-fill form fields from planned event
                  setFormData(prev => ({
                    ...prev,
                    plannedEventId: newValue.plannedEventId,
                    eventName: newValue.eventName,
                    startTime: newValue.startTime,
                    endTime: newValue.endTime,
                    eventTypeId: newValue.eventTypeId,
                    venueId: newValue.venueId || prev.venueId,
                    description: newValue.description || prev.description,
                    maxParticipants: newValue.maxParticipants,
                    requiredTables: newValue.requiredTables,
                  }));

                  // Load participants from the planned event
                  try {
                    console.log('Loading participants for planned event:', newValue.plannedEventId);
                    const participants = await plannedEventService.getParticipants(newValue.plannedEventId);
                    console.log('Loaded participants:', participants);

                    // Convert UserSummary to UserListItem and set selected users
                    const participantUsers = participants
                      .map(p => {
                        const user = users.find(u => u.userId === p.userId);
                        if (user) {
                          return user;
                        }
                        console.warn('Participant user not found in users list:', p.userId);
                        return null;
                      })
                      .filter(u => u !== null) as UserListItem[];

                    setSelectedUsers(participantUsers);

                    // Set role assignments from participants (all participants get the same role for now)
                    const roleAssignments: {[userId: string]: string} = {};
                    participantUsers.forEach(user => {
                      // Try to get the role from userRoles, default to first available role
                      const defaultRole = userRoles.find(r => r.name === 'Player') || userRoles[0];
                      if (defaultRole) {
                        roleAssignments[user.userId] = defaultRole.userRoleId;
                      }
                    });
                    setUserRoleAssignments(roleAssignments);

                    console.log('Set selected users and role assignments:', {
                      selectedUsers: participantUsers.length,
                      roleAssignments
                    });
                  } catch (err) {
                    console.error('Error loading participants:', err);
                    // Don't show error to user, just log it
                  }
                } else {
                  // Clear plannedEventId if deselected
                  setFormData(prev => ({ ...prev, plannedEventId: undefined }));
                  setSelectedUsers([]);
                  setUserRoleAssignments({});
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tervezett esemény sablon (opcionális)"
                  helperText="Válassz egy tervezett eseményt az adatok automatikus kitöltéséhez"
                />
              )}
              disabled={dataLoading}
              fullWidth
            />
            )}

            {/* Event Name */}
            <TextField
              label="Esemény neve"
              value={formData.eventName}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormData(prev => ({ ...prev, eventName: newValue }));

                // Clear validation error if the field now has valid content
                if (validationErrors.eventName && newValue.trim()) {
                  setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.eventName;
                    return newErrors;
                  });
                }
              }}
              error={!!validationErrors.eventName}
              helperText={validationErrors.eventName}
              fullWidth
              required
              disabled={dataLoading}
            />

            {/* Venue - moved to the top for better UX */}
            <FormControl fullWidth error={!!validationErrors.venueId}>
              <InputLabel>Helyszín *</InputLabel>
              <Select
                value={formData.venueId}
                label="Helyszín *"
                onChange={(e) => {
                  const newVenueId = e.target.value;
                  console.log('Venue changed to:', newVenueId);

                  // Update the form data first
                  setFormData(prev => ({ ...prev, venueId: newVenueId }));

                  // Clear venue validation error if a venue is selected
                  if (newVenueId && validationErrors.venueId) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.venueId;
                      return newErrors;
                    });
                  }

                  // Immediately trigger table calculation with new venue ID
                  if (newVenueId && formData.eventDate && formData.startTime && formData.endTime) {
                    console.log('Venue changed, triggering table recalculation');
                    const updatedFormData = { ...formData, venueId: newVenueId };
                    calculateAvailableTables(updatedFormData).then(() => {
                      // Re-validate required tables after venue change
                      if (formData.requiredTables > 0 && validationErrors.requiredTables) {
                        const updatedFormDataFinal = { ...formData, venueId: newVenueId };
                        if (validateRequiredTablesWithData(formData.requiredTables, updatedFormDataFinal)) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.requiredTables;
                            return newErrors;
                          });
                        }
                      }
                    });
                  } else {
                    // Clear available tables if venue is cleared or other fields are missing
                    setAvailableTables({});
                  }
                }}
                disabled={dataLoading}
              >
                {venues.map((venue) => (
                  <MenuItem key={venue.venueId} value={venue.venueId}>
                    {venue.name} ({venue.tableCount || 10} asztal)
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.venueId && (
                <Typography variant="caption" color="error">
                  {validationErrors.venueId}
                </Typography>
              )}
            </FormControl>

            {/* Maximum Participants and Required Tables */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Maximum résztvevők száma"
                type="number"
                // `?? ''`, NEM `|| ''` [N-12, 2026-09-14]: a `0 || ''` ÜRES mezőt ad, miközben az
                // állapotban 0 van — a felhasználó érintetlennek LÁTJA az űrlapot, a
                // mentetlen-változás védelem viszont piszkosnak jelenti, és a bezárásra rákérdez.
                // A hamis riasztás a védelem legrosszabb hibája: megszokja, és átkattint rajta.
                value={formData.maxParticipants ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                  setFormData(prev => ({ ...prev, maxParticipants: value }));
                }}
                error={!!validationErrors.maxParticipants}
                helperText={validationErrors.maxParticipants || "Üresen hagyva korlátlan. Magánóránál alapból 1 fő."}
                fullWidth
                disabled={dataLoading}
                inputProps={{
                  min: 1,
                  max: 99
                }}
              />
              <TextField
                label="Szükséges asztalok száma"
                type="number"
                // `?? ''`, NEM `|| ''` [N-12] — lásd a fenti mezőt. (A 0 asztal amúgy is
                // érvénytelen a szerveren: `[Range(1, int.MaxValue)]`, tehát a 0-t LÁTNI kell.)
                value={formData.requiredTables ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    // Allow empty field during typing
                    setFormData(prev => ({ ...prev, requiredTables: '' as any }));
                  } else {
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setFormData(prev => ({ ...prev, requiredTables: numValue }));

                      // Clear validation error if the value is now valid
                      if (validationErrors.requiredTables) {
                        // Validate the new value in real-time
                        const isValid = validateRequiredTables(numValue);
                        if (isValid) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.requiredTables;
                            return newErrors;
                          });
                        }
                      }
                    }
                  }
                }}
                error={!!validationErrors.requiredTables}
                helperText={validationErrors.requiredTables ||
                  (formData.venueId && venues.find(v => v.venueId === formData.venueId) ?
                    (() => {
                      const selectedVenue = venues.find(v => v.venueId === formData.venueId);
                      const totalTables = selectedVenue?.tableCount || 10;
                      const currentlyAvailable = availableTables[formData.venueId] !== undefined ?
                        availableTables[formData.venueId] : totalTables;
                      return `${totalTables}/${currentlyAvailable} asztal`;
                    })() :
                    "0 = nincs asztal foglalás")}
                fullWidth
                disabled={dataLoading}
                inputProps={{
                  min: 0,
                  max: formData.venueId ? (() => {
                    const selectedVenue = venues.find(v => v.venueId === formData.venueId);
                    const currentlyAvailable = availableTables[formData.venueId] !== undefined ?
                      availableTables[formData.venueId] : (selectedVenue?.tableCount || 10);
                    return currentlyAvailable;
                  })() : 99
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setFormData(prev => ({ ...prev, requiredTables: 0 }));
                    // Clear validation error when field is cleared
                    if (validationErrors.requiredTables) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.requiredTables;
                        return newErrors;
                      });
                    }
                  }
                }}
              />
            </Box>

            {/* Date and Time */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}>
              <DraftDatePicker
                label="Dátum"
                value={formData.eventDate || ''}
                syncKey={open}
                onDraftStatusChange={(status) => {
                  setDateDraft(prev => ({ ...prev, eventDate: status }));
                  if (status === 'valid') setValidationErrors(prev => { const n = { ...prev }; delete n.eventDate; return n; });
                }}
                slotProps={{ textField: { error: !!validationErrors.eventDate, helperText: validationErrors.eventDate } }}
                onChange={(newDate) => {
                  if (newDate) {
                    setFormData(prev => ({ ...prev, eventDate: newDate }));

                    // Re-validate required tables after date change
                    setTimeout(() => {
                      if (formData.requiredTables > 0 && validationErrors.requiredTables) {
                        const updatedFormData = { ...formData, eventDate: newDate };
                        if (validateRequiredTablesWithData(formData.requiredTables, updatedFormData)) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.requiredTables;
                            return newErrors;
                          });
                        }
                      }
                    }, 100); // Small delay to allow table calculation to complete
                  }
                }}
                disabled={dataLoading || isRecurring}
                sx={{ flex: 1 }}
              />
              <TimePicker
                label="Kezdés"
                value={parseTimeString(formData.startTime)}
                onChange={(time) => {
                  if (time && dayjs.isDayjs(time)) {
                    const newStartTime = formatTimeString(time);
                    setFormData(prev => ({ ...prev, startTime: newStartTime }));

                    // Re-validate required tables after start time change
                    setTimeout(() => {
                      if (formData.requiredTables > 0 && validationErrors.requiredTables) {
                        const updatedFormData = { ...formData, startTime: newStartTime };
                        if (validateRequiredTablesWithData(formData.requiredTables, updatedFormData)) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.requiredTables;
                            return newErrors;
                          });
                        }
                      }
                    }, 100); // Small delay to allow table calculation to complete
                  }
                }}
                ampm={false}
                sx={{ flex: 1 }}
              />
              <TimePicker
                label="Befejezés"
                value={parseTimeString(formData.endTime)}
                onChange={(time) => {
                  if (time && dayjs.isDayjs(time)) {
                    const newEndTime = formatTimeString(time);
                    setFormData(prev => ({ ...prev, endTime: newEndTime }));

                    // Clear end time validation error
                    if (validationErrors.endTime) {
                      const startTime = new Date(`2000-01-01T${formData.startTime}`);
                      const endTime = new Date(`2000-01-01T${newEndTime}`);
                      if (startTime < endTime) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.endTime;
                          return newErrors;
                        });
                      }
                    }

                    // Re-validate required tables after end time change
                    setTimeout(() => {
                      if (formData.requiredTables > 0 && validationErrors.requiredTables) {
                        const updatedFormData = { ...formData, endTime: newEndTime };
                        if (validateRequiredTablesWithData(formData.requiredTables, updatedFormData)) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.requiredTables;
                            return newErrors;
                          });
                        }
                      }
                    }, 100); // Small delay to allow table calculation to complete
                  }
                }}
                ampm={false}
                slotProps={{
                  textField: {
                    error: !!validationErrors.endTime
                  }
                }}
                sx={{ flex: 1 }}
              />
            </Box>
            {validationErrors.endTime && (
              <Typography variant="caption" color="error" sx={{ mt: -1 }}>
                {validationErrors.endTime}
              </Typography>
            )}

            {/* Ismétlődés — [magánóra-sorozat, 2026-10-31] adminnak ÉS edzőnek (edzőnél „Ismétlődő magánóra";
                korábban edzőnek rejtve volt, mert a szerver a sorozatot edzőtől 403-mal utasította el). */}
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRecurring}
                    onChange={(e) => {
                      setIsRecurring(e.target.checked);
                      setSubmitConflicts([]);
                      if (!e.target.checked) {
                        // Clear recurring settings when disabled
                        setRecurringEndDate('');
                        setRecurringIntervalWeeks(1);
                        setSelectedDaysOfWeek([]);
                      } else if (isCoach) {
                        // Sorozatban az edző kötelező résztvevő — ha egyszeri módban eltávolította, visszakerül.
                        ensureSelfAsCoach();
                      }
                    }}
                    disabled={dataLoading}
                    inputProps={{ 'data-testid': 'event-create-recurring' } as any}
                  />
                }
                label={isCoach ? 'Ismétlődő magánóra' : 'Ismétlődő esemény'}
              />
            </Box>

            {isRecurring && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1
              }}>
                <DraftDatePicker
                  label="Ismétlődés végdátuma"
                  value={recurringEndDate}
                  syncKey={open}
                  onDraftStatusChange={(status) => setDateDraft(prev => ({ ...prev, recurringEndDate: status }))}
                  onChange={(until) => {
                    if (until) {
                      setRecurringEndDate(until);
                      // Clear validation error when date is selected
                      if (validationErrors.recurringEndDate) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.recurringEndDate;
                          return newErrors;
                        });
                      }
                    }
                  }}
                  minDate={formData.eventDate ? dayjs(formData.eventDate).add(1, 'day') : undefined}
                  disabled={dataLoading}
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!validationErrors.recurringEndDate,
                      helperText: validationErrors.recurringEndDate || `Válaszd ki az utolsó napot, amikor még legyen alkalom. Legfeljebb ${MAX_SERIES_OCCURRENCES} alkalom.`
                    }
                  }}
                />

                <FormControl fullWidth disabled={dataLoading}>
                  <InputLabel id="recurring-interval-label">Ismétlődés gyakorisága</InputLabel>
                  <Select
                    labelId="recurring-interval-label"
                    label="Ismétlődés gyakorisága"
                    value={recurringIntervalWeeks}
                    onChange={(e) => setRecurringIntervalWeeks(Number(e.target.value))}
                  >
                    <MenuItem value={1}>Minden héten</MenuItem>
                    <MenuItem value={2}>Kéthetente</MenuItem>
                    <MenuItem value={3}>Háromhetente</MenuItem>
                    <MenuItem value={4}>Négyhetente</MenuItem>
                  </Select>
                  <FormHelperText>
                    A hetek a kezdő dátum hetétől számolódnak. Kéthetente = a kezdés hete, majd
                    minden második hét.
                  </FormHelperText>
                </FormControl>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Válaszd ki a hét napjait:
                  </Typography>
                  <FormGroup row>
                    {DAY_NAMES_MONDAY_FIRST.map((day) => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={selectedDaysOfWeek.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDaysOfWeek(prev => [...prev, day]);
                                // Clear validation error when at least one day is selected
                                if (validationErrors.selectedDaysOfWeek) {
                                  setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors.selectedDaysOfWeek;
                                    return newErrors;
                                  });
                                }
                              } else {
                                setSelectedDaysOfWeek(prev => prev.filter(d => d !== day));
                              }
                            }}
                            disabled={dataLoading}
                          />
                        }
                        label={day}
                      />
                    ))}
                  </FormGroup>
                  {validationErrors.selectedDaysOfWeek && (
                    <Typography variant="caption" color="error">
                      {validationErrors.selectedDaysOfWeek}
                    </Typography>
                  )}
                </Box>

                {/* [magánóra-sorozat, 2026-10-31] Előnézet a szerver `dryRun`-jából (késleltetve), ütközésnél a mentés tiltva. */}
                <Typography variant="caption" color="text.secondary" data-testid="series-max-hint">
                  Legfeljebb {MAX_SERIES_OCCURRENCES} alkalom. Ha bármelyik nap ütközik, a sorozat nem jön létre — válassz más napot vagy időpontot.
                </Typography>
                <SeriesPreviewAlert
                  state={seriesPreview}
                  testId="series-create-preview"
                  idleHint="Az előnézethez add meg a nevet, a helyszínt, a típust, a résztvevőket, a napokat és az „eddig” dátumot."
                />
              </Box>
            )}

            {/* Event Type */}
            <FormControl fullWidth error={!!validationErrors.eventTypeId}>
              <InputLabel>Esemény típusa *</InputLabel>
              <Select
                value={formData.eventTypeId}
                label="Esemény típusa *"
                onChange={(e) => {
                  const newValue = e.target.value;
                  // MAGÁNÓRÁNÁL alapból 1 fő, hogy a létrehozónak ne kelljen külön
                  // ráböknie. Korlát nélkül a rendszer „korlátlan"-nak látná, sosem
                  // telne be, és a játékosok naptára megtelne idegen órákkal.
                  // Csak akkor töltjük ki, ha a mező még üres — a kézi értéket nem
                  // írjuk felül.
                  const isPrivateLesson =
                    eventTypes.find(t => t.eventTypeId === newValue)?.name === 'IndividualTraining';
                  setFormData(prev => ({
                    ...prev,
                    eventTypeId: newValue,
                    maxParticipants:
                      isPrivateLesson && prev.maxParticipants === undefined
                        ? 1
                        : prev.maxParticipants,
                  }));

                  // Clear validation error if an event type is selected
                  if (validationErrors.eventTypeId && newValue) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.eventTypeId;
                      return newErrors;
                    });
                  }
                }}
                disabled={dataLoading}
              >
                {visibleEventTypes.map((type) => (
                  <MenuItem key={type.eventTypeId} value={type.eventTypeId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: type.color,
                          borderRadius: '50%',
                        }}
                      />
                      {type.displayName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.eventTypeId && (
                <Typography variant="caption" color="error">
                  {validationErrors.eventTypeId}
                </Typography>
              )}
            </FormControl>

            {/* Description */}
            <TextField
              label="Leírás"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  maxHeight: '120px',
                  overflow: 'auto',
                },
                '& .MuiInputBase-inputMultiline': {
                  resize: 'vertical',
                  minHeight: '20px',
                }
              }}
            />

            {/* Participants */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Résztvevők *
              </Typography>
              {/* [C10, 2026-10-30] MI VOLT A BAJ: a címke `${user.name} (${user.email})` volt — a szerver
                    edzőnek MÁS tag e-mailjét `null`-ként adja (`backend/PinyoApi/Services/UserListSanitizer.cs`),
                    így „Név (null)" lett volna. `userOptionLabel`: e-mail nélkül csak a név (admin: változatlan).
                    A sor-kulcs és az azonosság a `userId`-n áll, NEM a címkén/e-mailen (azonos nevű tagok). */}
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(user) => userOptionLabel(user)}
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                renderOption={(props, user) => {
                  const { key, ...optionProps } = props as any;
                  return (
                    <li key={user.userId} {...optionProps}>
                      {userOptionLabel(user)}
                    </li>
                  );
                }}
                value={selectedUsers}
                onChange={(_, newUsers) => handleUserSelect(newUsers)}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key: _tagKey, ...tagProps } = getTagProps({ index }) as any;
                    if (selfLocked && option.userId === selfId) {
                      // Sorozatban az edző saját chipje NEM törölhető (nincs törlő ikon) — a magyarázat súgóban.
                      return (
                        <Tooltip key={option.userId} title="Ismétlődő magánórán te vagy az edző — nem távolíthatod el magad.">
                          <Chip {...tagProps} label={option.name} onDelete={undefined} data-testid="participant-self-locked" />
                        </Tooltip>
                      );
                    }
                    return <Chip {...tagProps} label={option.name} key={option.userId} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Válasszon résztvevőket"
                    error={!!validationErrors.participants}
                    helperText={validationErrors.participants
                      || (selfLocked ? 'Ismétlődő magánórán te vagy az edző — magadat nem távolíthatod el.' : undefined)}
                  />
                )}
              />

              {/* Role assignments for selected users */}
              {selectedUsers.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Szerepkör hozzárendelés:
                  </Typography>
                  {selectedUsers.map((user) => (
                    <Box key={user.userId} sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                      mb: 1
                    }}>
                      <Typography variant="body2" sx={{
                        minWidth: { xs: 'auto', sm: 200 },
                        fontSize: { xs: '0.875rem', sm: '0.875rem' }
                      }}>
                        {user.name}
                      </Typography>
                      <FormControl size="small" sx={{
                        minWidth: { xs: 'auto', sm: 150 },
                        width: { xs: '100%', sm: 'auto' }
                      }}>
                        <InputLabel>Szerepkör</InputLabel>
                        <Select
                          value={userRoleAssignments[user.userId] || ''}
                          label="Szerepkör"
                          onChange={(e) => handleUserRoleChange(user.userId, e.target.value)}
                          // Sorozatban az edző saját szerepköre rögzített (Coach) — a szerver csak így engedi.
                          disabled={selfLocked && user.userId === selfId}
                        >
                          {userRoles
                            .filter(role => role.name === 'Coach' || role.name === 'Player')
                            .map((role) => (
                            <MenuItem key={role.userRoleId} value={role.userRoleId}>
                              {role.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Mégsem
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            // [magánóra-sorozat, 2026-10-31] Ütközésnél / 400-as előnézetnél (pl. 26 fölött) a mentés tiltva.
            disabled={loading || dataLoading || saveBlockedBySeries}
            data-testid="event-create-submit"
          >
            {loading ? 'Létrehozás...' : dataLoading ? 'Adatok betöltése...' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EventCreateDialog;